import { Briefcase_01 } from '@recruit-robin/robin-theme/web-icons';
import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

import type { Vacancy } from 'clients/AutoCompleteClient/types';

import { useCurrentUserQuery } from '@/shared/legacy/hooks/useCurrentUserQuery';
import { useConnectToAts } from 'components/ui/organisms/SelectedCampaign/TabAdditionalButtons/useConnectToAts';
import { SelectVacancyFromListModal } from 'components/ui/organisms/SelectVacancyFromListModal/SelectVacancyFromListModal';
import { useEnrichedVacanciesQuery } from 'hooks/reachout/useEnrichedVacanciesQuery';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SelectAtsVacancyModalType } from './SelectAtsVacancyForCampaignModal.types';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useGlobalDialogData } from 'stores/global-dialogs/hooks/useGlobalDialogData';
import { useIsGlobalDialogOpen } from 'stores/global-dialogs/hooks/useIsGlobalDialogOpen';

export const SelectAtsVacancyForCampaignModal = memo(() => {
  const selectVacancyModal = useGlobalDialog(GlobalDialogType.SelectAtsVacancyForCampaign);
  const isVisible = useIsGlobalDialogOpen(GlobalDialogType.SelectAtsVacancyForCampaign);
  const { dictionary } = useContext(LocalizationContext);
  const { onSelectAts } = useConnectToAts();

  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [selectedVacancy, setSelectedVacancy] = useState<Vacancy | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useDebounce(
    () => {
      setSearchDebounced(search);
    },
    230,
    [search],
  );

  useEffect(() => {
    if (!isVisible) {
      setSelectedVacancy(null);
      setSearch('');
      setSearchDebounced('');
    }
  }, [isVisible]);

  const { data: currentUser } = useCurrentUserQuery();
  const { data: vacancyList = [], isLoading: isVacanciesLoading } = useEnrichedVacanciesQuery({
    search: searchDebounced,
    atsOnly: true,
    enabled: isVisible && !!currentUser,
  });

  const dialogData = useGlobalDialogData({
    dialogType: GlobalDialogType.SelectAtsVacancyForCampaign,
    isVisible,
  });

  const handleClose = useCallback(() => {
    selectVacancyModal.close();
    dialogData?.onClose?.();
  }, [dialogData, selectVacancyModal]);

  const handleCloseButtonClick = useCallback(() => {
    selectVacancyModal.close();
    dialogData?.onCloseButtonClick?.();
  }, [dialogData, selectVacancyModal]);

  const handleConfirm = useCallback(async () => {
    if (!selectedVacancy || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onSelectAts(selectedVacancy);
      if (dialogData?.onConfirm) {
        await dialogData.onConfirm(selectedVacancy);
      }
      selectVacancyModal.close();
    } finally {
      setIsSubmitting(false);
    }
  }, [dialogData, isSubmitting, onSelectAts, selectVacancyModal, selectedVacancy]);

  const handleSelectVacancy = useCallback(
    (vacancy: Vacancy | null) => {
      setSelectedVacancy(vacancy);
    },
    [setSelectedVacancy],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const candidateFirstName = dialogData?.candidateFirstName || dictionary.theCandidate;
  const atsName = dialogData?.atsName || dictionary.yourAts;
  const modalType = dialogData?.type ?? SelectAtsVacancyModalType.Contacting;

  const { subtitle, cancelButtonLabel, confirmButtonLabel } = (() => {
    switch (modalType) {
      case SelectAtsVacancyModalType.Matching:
        return {
          subtitle: dictionary.selectAtsVacancyForMatching(candidateFirstName, atsName),
          cancelButtonLabel: undefined,
          confirmButtonLabel: dictionary.matchAndPushToAts,
        };
      case SelectAtsVacancyModalType.Scheduling:
        return {
          subtitle: dictionary.selectAtsVacancyForScheduling(candidateFirstName, atsName),
          cancelButtonLabel: dictionary.skipAndSchedule,
          confirmButtonLabel: dictionary.connectAndSchedule,
        };
      default:
        return {
          subtitle: dictionary.selectAtsVacancyRobinJr(candidateFirstName, atsName),
          cancelButtonLabel: dictionary.skipAndSendMessage,
          confirmButtonLabel: dictionary.sendAndPushToAts,
        };
    }
  })();

  return (
    <SelectVacancyFromListModal
      isVisible={isVisible}
      onClose={handleClose}
      icon={<Briefcase_01 size={24} color={(c) => c.primary.fuchsia[40]} />}
      title={dictionary.selectAtsVacancyFirst(candidateFirstName, atsName)}
      subtitle={subtitle}
      onSearchChange={handleSearchChange}
      vacancyList={vacancyList}
      selectedVacancy={selectedVacancy}
      onSelectVacancy={handleSelectVacancy}
      onConfirm={handleConfirm}
      isLoading={isSubmitting}
      isVacanciesLoading={isVacanciesLoading}
      cancelButtonLabel={cancelButtonLabel}
      confirmButtonLabel={confirmButtonLabel}
      onCloseButtonClick={handleCloseButtonClick}
    />
  );
});
